import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import FirstPageOutlined from '@mui/icons-material/FirstPageOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode } from 'react';

import { Pagination, PaginationLink } from '~services/pagination';

interface DataGridPagerProps {
  isFetching: boolean;
  // Called with the direction to move, or undefined to return to the first page —
  // which for a cursor API means sending no cursor at all.
  onNavigate: (direction?: PaginationLink) => void;
  pagination?: Pagination;
}

interface PagerSegmentProps {
  ariaLabel: string;
  children: ReactNode;
  isDisabled: boolean;
  onClick: () => void;
}

function PagerDivider() {
  return (
    <Box sx={(theme) => ({ backgroundColor: theme.semantic.border, width: '1px' })} />
  );
}

function PagerSegment({ ariaLabel, children, isDisabled, onClick }: PagerSegmentProps) {
  return (
    <ButtonBase
      aria-label={ariaLabel}
      disabled={isDisabled}
      onClick={onClick}
      sx={(theme) => ({
        alignItems: 'center',
        color: isDisabled ? theme.semantic.textDisabled : theme.semantic.textPrimary,
        display: 'flex',
        fontSize: '13px',
        fontWeight: 600,
        gap: 0.75,
        px: 1.5,
        '&:hover': { backgroundColor: theme.semantic.surfaceSunken },
      })}
    >
      {children}
    </ButtonBase>
  );
}

/**
 * Cursor pager for the shared DataGrid, rendered as trailing toolbar content beside
 * the quick filter.
 *
 * There is no total count or page count on these endpoints — only after/before
 * cursors — so the control deliberately offers no last-page jump and no row range.
 */
export default function DataGridPager({
  isFetching,
  onNavigate,
  pagination,
}: DataGridPagerProps) {
  const isOnFirstPage = !pagination?.before;
  // Not every endpoint returns a page number — `extractPagination` reads only
  // the before/after cursors — so claim page one only when the absence of a
  // before-cursor proves it, rather than reporting 1 from an unknown page.
  const page = pagination?.page ?? (isOnFirstPage ? 1 : undefined);
  const hasNextPage = Boolean(pagination?.after);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Box
        sx={(theme) => ({
          alignItems: 'stretch',
          border: `1px solid ${theme.semantic.borderStrong}`,
          borderRadius: 1,
          display: 'flex',
          // Geometry from the paging redesign: a 34px control, a 2px underline
          // marking the page cell, and a 9px eyebrow above the number.
          height: '34px',
          overflow: 'hidden',
        })}
      >
        <PagerSegment
          ariaLabel={t('actions.first_page')}
          isDisabled={isFetching || isOnFirstPage}
          onClick={() => onNavigate()}
        >
          <FirstPageOutlined sx={{ fontSize: '14px' }} />
          {t('actions.first')}
        </PagerSegment>

        <PagerDivider />

        <PagerSegment
          ariaLabel={t('actions.previous_page')}
          isDisabled={isFetching || isOnFirstPage}
          onClick={() => onNavigate('before')}
        >
          <ChevronLeftOutlined sx={{ fontSize: '14px' }} />
          {t('actions.previous')}
        </PagerSegment>

        <PagerDivider />

        <Box
          sx={(theme) => ({
            alignItems: 'center',
            backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.1),
            borderBottom: `2px solid ${theme.brandV2.colors.treadYellow}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2,
          })}
        >
          <Typography
            sx={(theme) => ({
              color: theme.semantic.textSecondary,
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.10em',
              lineHeight: 1,
              textTransform: 'uppercase',
            })}
          >
            {t('common.page')}
          </Typography>
          <Typography
            sx={(theme) => ({
              color: theme.semantic.textPrimary,
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: 1.2,
            })}
          >
            {page ?? '–'}
          </Typography>
        </Box>

        <PagerDivider />

        <PagerSegment
          ariaLabel={t('actions.next_page')}
          isDisabled={isFetching || !hasNextPage}
          onClick={() => onNavigate('after')}
        >
          {t('actions.next')}
          <ChevronRightOutlined sx={{ fontSize: '14px' }} />
        </PagerSegment>
      </Box>
    </Box>
  );
}
